import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNotify } from 'react-admin';
import {
    Grid,
    Box,
    Typography,
    Paper,
    Autocomplete,
    TextField,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    LinearProgress,
    Chip,
    Avatar,
    Button,
    ButtonGroup,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
} from 'recharts';
import SchoolIcon from '@mui/icons-material/School';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import { StatCard } from './StatCard';

interface User {
    id: number;
    email: string;
    firstName?: string;
    lastName?: string;
}

interface UserDetailDashboardProps {
    dateParams: { from: string; to: string };
    groupBy: string;
    companyId?: number | null;
    departmentId?: number | null;
    groupId?: number | null;
}

const baseApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_URL = baseApiUrl.endsWith('/api') ? baseApiUrl : `${baseApiUrl}/api`;

const COLORS = ['#4caf50', '#1976d2', '#ff9800', '#f44336'];

const isAssessmentPassed = (status?: string): boolean => status === 'passed';

const formatTime = (seconds: number): string => {
    if (!seconds) return '0m';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
        return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
};

export const UserDetailDashboard: React.FC<UserDetailDashboardProps> = ({
    dateParams,
    groupBy,
    companyId,
    departmentId,
    groupId,
}) => {
    const notify = useNotify();
    const [users, setUsers] = useState<User[]>([]);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [searchInput, setSearchInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [usersLoading, setUsersLoading] = useState(false);
    const [data, setData] = useState<any>(null);

    const getToken = () => localStorage.getItem('token');

    // Search users
    const searchUsers = useCallback(async (search: string) => {
        if (search.length < 2) {
            setUsers([]);
            return;
        }

        const token = getToken();
        if (!token) return;

        setUsersLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('search', search);
            params.append('itemsPerPage', '20');
            if (companyId) params.append('companyId', companyId.toString());

            // Use the analytics users endpoint (proxy to SSO)
            const response = await fetch(`${API_URL}/analytics/users?${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/ld+json',
                },
            });

            if (response.ok) {
                const result = await response.json();
                const userList = (result['hydra:member'] || result.member || []).map((u: any) => ({
                    id: u.id,
                    email: u.email,
                    firstName: u.firstName,
                    lastName: u.lastName,
                }));
                setUsers(userList);
            }
        } catch (err) {
            console.error('Error searching users:', err);
        } finally {
            setUsersLoading(false);
        }
    }, [companyId]);

    useEffect(() => {
        const debounce = setTimeout(() => {
            searchUsers(searchInput);
        }, 300);
        return () => clearTimeout(debounce);
    }, [searchInput, searchUsers]);

    // Load user detail data
    const latestLoad = useRef(0);

    const loadUserData = useCallback(async () => {
        const loadId = ++latestLoad.current;
        if (!selectedUser) {
            setData(null);
            setLoading(false);
            return;
        }

        const token = getToken();
        if (!token) return;

        setLoading(true);
        try {
            const params = new URLSearchParams({
                from: dateParams.from,
                to: dateParams.to,
                groupBy,
            });
            if (companyId) params.append('companyId', companyId.toString());
            if (departmentId) params.append('departmentId', departmentId.toString());
            if (groupId) params.append('groupId', groupId.toString());

            const response = await fetch(
                `${API_URL}/analytics/reports/user/${selectedUser.id}?${params}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept': 'application/json',
                    },
                }
            );

            if (response.ok) {
                const result = await response.json();
                if (loadId !== latestLoad.current) return;
                setData(result);
            } else {
                if (loadId !== latestLoad.current) return;
                console.error('Failed to load user data');
                setData(null);
                notify('Nutzerdaten konnten nicht geladen werden.', { type: 'error' });
            }
        } catch (err) {
            if (loadId !== latestLoad.current) return;
            console.error('Error loading user data:', err);
            setData(null);
            notify('Nutzerdaten konnten nicht geladen werden.', { type: 'error' });
        } finally {
            if (loadId === latestLoad.current) {
                setLoading(false);
            }
        }
    }, [selectedUser, dateParams, groupBy, companyId, departmentId, groupId, notify]);

    useEffect(() => {
        loadUserData();
    }, [loadUserData]);

    // Export handler
    const handleExport = async (exportFormat: 'pdf' | 'csv' | 'excel') => {
        if (!selectedUser) return;

        const token = getToken();
        if (!token) return;

        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/analytics/reports/export/${exportFormat}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': exportFormat === 'pdf' ? 'application/pdf' :
                             exportFormat === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' :
                             'text/csv',
                },
                body: JSON.stringify({
                    reportType: 'user-detail',
                    entityId: selectedUser.id,
                    filters: { from: dateParams.from, to: dateParams.to, groupBy, companyId, departmentId, groupId },
                }),
            });

            if (!response.ok) {
                throw new Error('Export failed');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const ext = exportFormat === 'pdf' ? 'pdf' : exportFormat === 'excel' ? 'xlsx' : 'csv';
            a.download = `nutzer-detail-${selectedUser.id}.${ext}`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            console.error('Export error:', err);
        } finally {
            setLoading(false);
        }
    };

    const getUserDisplayName = (user: User) => {
        if (user.firstName || user.lastName) {
            return `${user.firstName || ''} ${user.lastName || ''}`.trim();
        }
        return user.email;
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed':
                return 'success';
            case 'in_progress':
                return 'primary';
            case 'not_started':
                return 'default';
            default:
                return 'default';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'completed':
                return 'Abgeschlossen';
            case 'in_progress':
                return 'In Bearbeitung';
            case 'not_started':
                return 'Nicht gestartet';
            case 'enrolled':
                return 'Eingeschrieben';
            case 'failed':
                return 'Nicht bestanden';
            case 'suspended':
                return 'Pausiert';
            case 'pending_approval':
                return 'Warten auf Freigabe';
            case 'rejected':
                return 'Abgelehnt';
            default:
                return status;
        }
    };

    // Prepare pie chart data for enrollments by status
    const getEnrollmentStatusData = () => {
        if (!data?.enrollments) return [];
        const statusCounts: Record<string, number> = {};
        data.enrollments.forEach((e: any) => {
            statusCounts[e.status] = (statusCounts[e.status] || 0) + 1;
        });
        return Object.entries(statusCounts).map(([status, count]) => ({
            name: getStatusLabel(status),
            value: count,
        }));
    };

    return (
        <Box sx={{ px: 2 }}>
            {/* User Search */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ flexGrow: 1, maxWidth: 500 }}>
                        <Typography variant="h6" gutterBottom>
                            Nutzer auswählen
                        </Typography>
                        <Autocomplete
                            options={users}
                            getOptionLabel={(option) => getUserDisplayName(option)}
                            value={selectedUser}
                            onChange={(_, newValue) => setSelectedUser(newValue)}
                            onInputChange={(_, newInputValue) => setSearchInput(newInputValue)}
                            loading={usersLoading}
                            filterOptions={(x) => x} // Disable client-side filtering
                            renderOption={(props, option) => (
                                <li {...props} key={option.id}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography>{getUserDisplayName(option)}</Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {option.email}
                                        </Typography>
                                    </Box>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Nutzer suchen"
                                    placeholder="Name oder E-Mail eingeben..."
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {usersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Box>
                    {selectedUser && data && (
                        <ButtonGroup variant="outlined" size="small">
                            <Button
                                startIcon={<DownloadIcon />}
                                onClick={() => handleExport('pdf')}
                                disabled={loading}
                            >
                                PDF
                            </Button>
                            <Button
                                onClick={() => handleExport('csv')}
                                disabled={loading}
                            >
                                CSV
                            </Button>
                            <Button
                                onClick={() => handleExport('excel')}
                                disabled={loading}
                            >
                                Excel
                            </Button>
                        </ButtonGroup>
                    )}
                </Box>
            </Paper>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {!loading && !selectedUser && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="textSecondary">
                        Bitte suchen und wählen Sie einen Nutzer aus, um die Statistiken anzuzeigen.
                    </Typography>
                </Box>
            )}

            {!loading && selectedUser && data && (
                <>
                    {/* User Info Header */}
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main' }}>
                                {(data.user?.firstName?.[0] || data.user?.email?.[0] || 'U').toUpperCase()}
                            </Avatar>
                            <Box>
                                <Typography variant="h5">
                                    {data.user?.firstName} {data.user?.lastName}
                                </Typography>
                                <Typography color="textSecondary">
                                    {data.user?.email}
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>

                    {/* Summary Cards */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <StatCard
                                title="Eingeschriebene Kurse"
                                value={data.summary?.total_enrollments || 0}
                                icon={<SchoolIcon sx={{ fontSize: 48 }} />}
                                color="primary"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <StatCard
                                title="Abgeschlossen"
                                value={data.summary?.completed_courses || 0}
                                subtitle={`${parseFloat(data.summary?.completion_rate || 0).toFixed(1)}% Rate`}
                                icon={<CheckCircleIcon sx={{ fontSize: 48 }} />}
                                color="success"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <StatCard
                                title="Gesamte Lernzeit"
                                value={formatTime(data.summary?.total_time_seconds || 0)}
                                icon={<AccessTimeIcon sx={{ fontSize: 48 }} />}
                                color="info"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <StatCard
                                title="Aktuelle Serie"
                                value={`${data.streak?.current_streak || 0} Tage`}
                                subtitle={`Längste: ${data.streak?.longest_streak || 0} Tage`}
                                icon={<LocalFireDepartmentIcon sx={{ fontSize: 48 }} />}
                                color="warning"
                            />
                        </Grid>
                    </Grid>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2, fontStyle: 'italic' }}>
                        Kennzahlen, Kurse und Prüfungen zeigen die gesamte Lernhistorie. Der gewählte Zeitraum gilt nur für den Aktivitätsverlauf.
                    </Typography>

                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        {/* Progress Over Time */}
                        <Grid size={{ xs: 12, md: 8 }}>
                            {data.progressOverTime && data.progressOverTime.length > 0 && (
                                <Paper sx={{ p: 3, height: '100%' }}>
                                    <Typography variant="h6" gutterBottom>
                                        Lernaktivität über Zeit
                                    </Typography>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <LineChart data={data.progressOverTime}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="period" />
                                            <YAxis />
                                            <Tooltip />
                                            <Legend />
                                            <Line
                                                type="monotone"
                                                dataKey="lessons_completed"
                                                name="Abgeschlossene Lektionen"
                                                stroke="#4caf50"
                                                strokeWidth={2}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="time_spent_minutes"
                                                name="Zeit (Min)"
                                                stroke="#1976d2"
                                                strokeWidth={2}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </Paper>
                            )}
                        </Grid>

                        {/* Enrollment Status Pie Chart */}
                        <Grid size={{ xs: 12, md: 4 }}>
                            {data.enrollments && data.enrollments.length > 0 && (
                                <Paper sx={{ p: 3, height: '100%' }}>
                                    <Typography variant="h6" gutterBottom>
                                        Kursstatus
                                    </Typography>
                                    <ResponsiveContainer width="100%" height={250}>
                                        <PieChart>
                                            <Pie
                                                data={getEnrollmentStatusData()}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={80}
                                                paddingAngle={5}
                                                dataKey="value"
                                            >
                                                {getEnrollmentStatusData().map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                            <Legend />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </Paper>
                            )}
                        </Grid>
                    </Grid>

                    {/* Enrollments Table */}
                    {data.enrollments && data.enrollments.length > 0 && (
                        <Paper sx={{ p: 3, mb: 4 }}>
                            <Typography variant="h6" gutterBottom>
                                Kurs-Einschreibungen
                            </Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Kurs</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Fortschritt</TableCell>
                                            <TableCell align="right">Score</TableCell>
                                            <TableCell>Eingeschrieben</TableCell>
                                            <TableCell>Abgeschlossen</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.enrollments.map((enrollment: any, index: number) => (
                                            <TableRow key={enrollment.course_id || index}>
                                                <TableCell>{enrollment.course_title}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={getStatusLabel(enrollment.status)}
                                                        color={getStatusColor(enrollment.status) as any}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ minWidth: 150 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={parseFloat(enrollment.progress_percentage || enrollment.progress || 0)}
                                                            sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                                                        />
                                                        <Typography variant="body2" sx={{ minWidth: 45 }}>
                                                            {parseFloat(enrollment.progress_percentage || enrollment.progress || 0).toFixed(0)}%
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="right">
                                                    {enrollment.score != null
                                                        ? `${parseFloat(enrollment.score).toFixed(1)}%`
                                                        : '-'}
                                                </TableCell>
                                                <TableCell>
                                                    {enrollment.enrolled_at
                                                        ? new Date(enrollment.enrolled_at).toLocaleDateString('de-DE')
                                                        : '-'}
                                                </TableCell>
                                                <TableCell>
                                                    {enrollment.completed_at
                                                        ? new Date(enrollment.completed_at).toLocaleDateString('de-DE')
                                                        : '-'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    )}

                    {/* Assessment Results */}
                    {data.assessmentResults && data.assessmentResults.length > 0 && (
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Assessment-Ergebnisse
                            </Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Assessment</TableCell>
                                            <TableCell>Kurs</TableCell>
                                            <TableCell align="right">Score</TableCell>
                                            <TableCell align="center">Bestanden</TableCell>
                                            <TableCell align="right">Versuche</TableCell>
                                            <TableCell>Datum</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.assessmentResults.map((assessment: any, index: number) => (
                                            <TableRow key={index}>
                                                <TableCell>{assessment.lesson_title || 'Assessment'}</TableCell>
                                                <TableCell>{assessment.course_title}</TableCell>
                                                <TableCell align="right">
                                                    <Typography
                                                        sx={{
                                                            color: isAssessmentPassed(assessment.status)
                                                                ? 'success.main'
                                                                : assessment.status === 'failed'
                                                                    ? 'error.main'
                                                                    : undefined,
                                                            fontWeight: 'bold',
                                                        }}
                                                    >
                                                        {assessment.score == null
                                                            ? '-'
                                                            : `${parseFloat(assessment.score).toFixed(1)}%`}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    {isAssessmentPassed(assessment.status) ? (
                                                        <CheckCircleIcon color="success" fontSize="small" />
                                                    ) : assessment.status === 'failed' ? (
                                                        <Typography color="error">X</Typography>
                                                    ) : (
                                                        '-'
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">{assessment.attempt_count || 1}</TableCell>
                                                <TableCell>
                                                    {assessment.completed_at
                                                        ? new Date(assessment.completed_at).toLocaleDateString('de-DE')
                                                        : '-'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    )}
                </>
            )}
        </Box>
    );
};

export default UserDetailDashboard;
